import { useEffect } from "react";
import axios from "axios";
import { useRecoilState, useSetRecoilState } from "recoil";
import { activeWorkspaceState, authState, featuresState } from "../Recoil/RecoilState";
import { baseURL } from "../utils/StaticVariables";

// Loads the user's system role and active workspace, then the features open to them
// in it, and reloads the features whenever the workspace changes.
const useWorkspaceContext = () => {
  const [authRecoil, setAuthRecoil] = useRecoilState(authState);
  const token = authRecoil?.token;
  const [activeWorkspace, setActiveWorkspace] = useRecoilState(activeWorkspaceState);
  const setFeatures = useSetRecoilState(featuresState);

  // The role saved at login can be missing or stale (older sessions, role changed by a
  // superadmin): take it from the server so superadmin-only menus show correctly.
  useEffect(() => {
    if (!token) return;
    axios
      .get(`${baseURL}user_info`, { headers: { Authorization: `Bearer ${token}` } })
      .then(({ data }) => {
        if (!data?.role) return;
        localStorage.setItem("role", data.role);
        setAuthRecoil((prev) => (prev && prev.role !== data.role ? { ...prev, role: data.role } : prev));
      })
      .catch(() => {});
  }, [token, setAuthRecoil]);

  useEffect(() => {
    if (!token) return;
    axios
      .get(`${baseURL}workspaces/active`, { headers: { Authorization: `Bearer ${token}` } })
      .then(({ data }) => {
        if (data) {
          setActiveWorkspace({
            workspace_id: data.workspace_id || data.id,
            name: data.name,
            member_role: data.member_role || data.role || data.user_role || "admin",
            enabled_features: data.enabled_features,
            ...data,
          });
        } else {
          throw new Error("No active workspace");
        }
      })
      .catch((err) => {
        // If 401, session is expired, do not chain another request
        if (err?.response?.status === 401) return;
        // Fallback to user workspaces if /workspaces/active is not available
        axios
          .get(`${baseURL}workspaces/user`, { headers: { Authorization: `Bearer ${token}` } })
          .then(({ data }) => {
            const list = Array.isArray(data) ? data : [];
            if (list.length > 0) {
              const first = list[0];
              setActiveWorkspace({
                workspace_id: first.workspace_id || first.id,
                name: first.name,
                member_role: first.member_role || first.role || first.user_role || "admin",
                enabled_features: first.enabled_features,
                ...first,
              });
            }
          })
          .catch(() => {});
      });
  }, [token, setActiveWorkspace]);

  const workspaceId = activeWorkspace?.workspace_id || activeWorkspace?.id;
  useEffect(() => {
    if (!token) return;
    axios
      .get(`${baseURL}features/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then(({ data }) => setFeatures(data?.features || []))
      // An older backend has no /features/me: don't hide anything.
      .catch((err) => {
        // 401: session is over — don't flash an empty menu on the way out.
        if (err?.response?.status === 401) return;
        setFeatures(err?.response?.status === 404 ? null : []);
      });
  }, [token, workspaceId, setFeatures]);
};

export default useWorkspaceContext;
